import { SnackbarService } from '@abb-procure/snackbar';
import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';
import { DialogService } from '../../services/dialog.service';
import { escapeDialog } from '../../utilities/dialog.util';
import { Button } from '../button/button.component';
import { InputTextArea } from '../input-textarea/input-textarea.component';

@Component({
  templateUrl: './status-dialog.component.html',
  styleUrl: './status-dialog.component.scss',
  imports: [
    ReactiveFormsModule,
    MatDialogContent,
    InputTextArea,
    Button,
    MatTooltip,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class StatusDialog implements OnInit {
  readonly form = inject(FormBuilder).group({
    comment: new FormControl('', {
      validators: [Validators.required],
      nonNullable: true,
    }),
  });

  private readonly dialogRef = inject(MatDialogRef<StatusDialog, string>);
  private readonly dialogService = inject(DialogService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    escapeDialog(this.dialogRef, this.destroyRef, () => this.onCancel());
  }

  /**
   * Handle form submission.
   */
  onSubmit(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      this.snackbarService.showValidationErrorSnackbar();

      return;
    }

    this.dialogRef.close(this.form.controls.comment.value);
  }

  /**
   * Handle dialog cancellation and ask for confirmation in case of edited
   * values.
   */
  onCancel(): void {
    if (this.form.pristine) {
      this.dialogRef.close();
      return;
    }

    const dialogRef = this.dialogService.showConfirmDialog();

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((closeConfirmed) => {
        if (!closeConfirmed) {
          return;
        }

        this.dialogRef.close();
      });
  }
}
